/*
  Custom Product Gallery (CPG) runtime for blocks/_product-media-gallery.liquid.

  Lives in an asset instead of the block's {% javascript %} because Shopify only
  bundles block JS on pages that render the block. Collection pages show the same
  gallery HTML inside the quick-add <dialog>, so assets/quick-add.js imports this
  module and calls initGalleries() on the modal content.
*/

class CustomProductGallery {
  constructor(root) {
    this.root = root;
    this.slides = Array.from(root.querySelectorAll('[data-cpg-slide]'));
    this.thumbs = Array.from(root.querySelectorAll('[data-cpg-thumb]'));
    this.ticks = Array.from(root.querySelectorAll('[data-cpg-tick]'));
    this.prevBtns = Array.from(root.querySelectorAll('[data-cpg-prev]'));
    this.nextBtns = Array.from(root.querySelectorAll('[data-cpg-next]'));
    this.numberCurrent = root.querySelector('[data-cpg-number-current]');
    this.stage = root.querySelector('[data-cpg-stage]');
    this.zoomModal = null;
    // The fullscreen viewer is appended to <body>, which renders below a top-layer <dialog>.
    this.inDialog = Boolean(root.closest('dialog'));
    this.zoomEnabled = root.dataset.zoomEnabled === 'true' && !this.inDialog;
    // 整屏看图里的图片列表：只收图片 slide（视频不进）
    this.zoomItems = [];
    this.zoomIndex = 0;

    this.currentIndex = 0;
    this.total = this.slides.length;
    root._cpg = this;

    this.autoplay = root.dataset.autoplay === 'true';
    this.autoplayInterval = parseInt(root.dataset.autoplayInterval, 10) || 5000;
    this.autoplayTimer = null;

    // JS 强制修复祖先元素的 overflow 遮挡
    this._fixStickyParents();

    // 为性能,缓存 thumb 按 index 分组
    this.thumbsByIndex = {};
    this.thumbs.forEach((t) => {
      const idx = parseInt(t.dataset.index, 10);
      if (!this.thumbsByIndex[idx]) this.thumbsByIndex[idx] = [];
      this.thumbsByIndex[idx].push(t);
    });

    this.bindVideoControls();
    this.bindExternalVideos();
    this.bindZoom();
    this._bindStripMode();

    if (this.total <= 1) return;

    this.bindNavButtons();
    this.bindThumbs();
    this.bindThumbDrag();
    this.bindTicks();
    this.bindTouch();

    // 非关键绑定延后
    this._deferNonCritical();
  }

  _fixStickyParents() {
    const wrapper = this.root;
    // 如果未开启 sticky 或在移动端，则跳过
    if (!wrapper.classList.contains('cpg-wrapper--sticky') || window.innerWidth < 750 || this.inDialog) return;

    // 向上递归解锁主题自带的 overflow: hidden 限制 (它是 sticky 失效的最大元凶)
    let parent = wrapper.parentElement;
    let depth = 0;
    // 最多向上寻找 8 层
    while (parent && parent.tagName !== 'BODY' && depth < 8) {
      const style = window.getComputedStyle(parent);
      if (style.overflow === 'hidden' || style.overflowY === 'hidden' || style.overflow === 'clip') {
         // 强制放开限制
         parent.style.setProperty('overflow', 'visible', 'important');
      }
      parent = parent.parentElement;
      depth++;
    }
  }

  _deferNonCritical() {
    const run = () => {
      this.bindKeyboard();
      if (this.autoplay) {
        this.startAutoplay();
        this.stage.addEventListener('mouseenter', () => this.stopAutoplay());
        this.stage.addEventListener('mouseleave', () => this.startAutoplay());
      }
      // 预加载相邻图片
      this._preloadAdjacent(0);
    };
    if ('requestIdleCallback' in window) {
      requestIdleCallback(run, { timeout: 1500 });
    } else {
      setTimeout(run, 800);
    }
  }

  _preloadAdjacent(currentIdx) {
    [currentIdx - 1, currentIdx + 1].forEach((index) => {
      const slide = this.slides[index];
      if (!slide) return;
      const img = slide.querySelector('img');
      if (img && img.loading === 'lazy') {
        img.loading = 'eager';
        if ('fetchPriority' in img) img.fetchPriority = 'low';
      }
    });
  }

  _isMobileStrip() {
    return this._stripQuery
      ? this._stripQuery.matches
      : window.matchMedia('(max-width: 749px)').matches;
  }

  /**
   * 手机横带：第 index 张贴在视口，drag 是手指偏移（px）。
   * 桌面不写 transform，淡入淡出照旧。
   */
  _placeTrack(index, { animate = false, drag = 0 } = {}) {
    if (!this.track || !this._isMobileStrip()) return;
    const width = this.stage ? this.stage.clientWidth : 0;
    if (!width) return;
    const x = Math.round(-index * width + drag);
    const transform = `translate3d(${x}px, 0, 0)`;
    if (!animate) {
      this.track.classList.remove('is-animating');
      this.track.style.transform = transform;
      return;
    }
    this.track.classList.add('is-animating');
    void this.track.offsetWidth;
    this.track.style.transform = transform;
  }

  _bindStripMode() {
    this.track = this.root.querySelector('[data-cpg-slides]');
    this._stripQuery = window.matchMedia('(max-width: 749px)');
    const sync = () => {
      if (!this.track) return;
      if (this._stripQuery.matches) {
        this._placeTrack(this.currentIndex, { animate: false });
      } else {
        this.track.classList.remove('is-animating');
        this.track.style.transform = '';
      }
    };
    this._stripQuery.addEventListener('change', sync);
    sync();
  }

  _armClickSuppress() {
    this._suppressClick = true;
    clearTimeout(this._suppressTimer);
    this._suppressTimer = setTimeout(() => {
      this._suppressClick = false;
    }, 450);
  }

  bindNavButtons() {
    this.prevBtns.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.go(this.currentIndex - 1);
        this.resetAutoplay();
      });
    });
    this.nextBtns.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.go(this.currentIndex + 1);
        this.resetAutoplay();
      });
    });
  }

  bindThumbs() {
    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', (e) => {
        e.stopPropagation();
        const idx = parseInt(thumb.dataset.index, 10);
        this.go(idx);
        this.resetAutoplay();
      });
      thumb.addEventListener('mouseenter', () => {
        if (this.thumbDragging) return;
        if (window.matchMedia('(hover: hover)').matches) {
          const idx = parseInt(thumb.dataset.index, 10);
          this.go(idx);
        }
      });
    });
  }

  /** Mouse drag-to-scroll for the external thumbnail strip. Touch already scrolls natively. */
  bindThumbDrag() {
    const strip = this.root.querySelector('[data-cpg-thumbs-external]');
    if (!strip) return;

    const overflows = () =>
      strip.scrollHeight > strip.clientHeight + 1 || strip.scrollWidth > strip.clientWidth + 1;
    let pointerId = null;
    let startX = 0;
    let startY = 0;
    let startLeft = 0;
    let startTop = 0;
    let suppressClick = false;
    this.thumbDragging = false;

    const onMove = (e) => {
      if (e.pointerId !== pointerId) return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      if (!this.thumbDragging) {
        if (Math.abs(dx) < 5 && Math.abs(dy) < 5) return;
        this.thumbDragging = true;
        strip.classList.add('is-dragging');
        try {
          strip.setPointerCapture(pointerId);
        } catch (err) {}
      }
      strip.scrollLeft = startLeft - dx;
      strip.scrollTop = startTop - dy;
    };

    const onEnd = (e) => {
      if (e.pointerId !== pointerId) return;
      pointerId = null;
      strip.removeEventListener('pointermove', onMove);
      strip.removeEventListener('pointerup', onEnd);
      strip.removeEventListener('pointercancel', onEnd);
      strip.classList.remove('is-dragging');
      if (!this.thumbDragging) return;
      this.thumbDragging = false;
      suppressClick = true;
      setTimeout(() => { suppressClick = false; }, 0);
    };

    strip.addEventListener('pointerenter', () => {
      strip.classList.toggle('is-draggable', overflows());
    });

    strip.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0 || !overflows()) return;
      pointerId = e.pointerId;
      startX = e.clientX;
      startY = e.clientY;
      startLeft = strip.scrollLeft;
      startTop = strip.scrollTop;
      strip.addEventListener('pointermove', onMove);
      strip.addEventListener('pointerup', onEnd);
      strip.addEventListener('pointercancel', onEnd);
    });

    // A drag ends with a click on whichever thumb is under the cursor; that must not select it.
    strip.addEventListener('click', (e) => {
      if (!suppressClick) return;
      e.preventDefault();
      e.stopPropagation();
    }, true);

    strip.addEventListener('dragstart', (e) => e.preventDefault());
  }

  bindTicks() {
    this.ticks.forEach((tick) => {
      tick.addEventListener('click', (e) => {
        e.stopPropagation();
        const idx = parseInt(tick.dataset.index, 10);
        this.go(idx);
        this.resetAutoplay();
      });
    });
  }

  bindVideoControls() {
    this.slides.forEach((slide) => {
      if (slide.dataset.type !== 'video') return;
      const video = slide.querySelector('[data-cpg-video]');
      // 角标按钮 + 封面正中的大播放按钮,都走同一套播放/暂停
      const toggles = slide.querySelectorAll('[data-cpg-video-toggle]');
      if (!video || !toggles.length) return;

      video.muted = true;

      toggles.forEach((toggle) => {
        toggle.addEventListener('click', (e) => {
          e.stopPropagation();
          if (video.paused) {
            video.play().catch(() => {});
            this.stopAutoplay();
          } else {
            video.pause();
          }
        });
      });

      const controls = slide.querySelector('[data-cpg-video-controls]');
      const setPlaying = (playing) => {
        toggles.forEach((t) => t.classList.toggle('is-playing', playing));
        if (controls) controls.classList.toggle('is-playing', playing);
        if (playing) slide.classList.add('is-video-playing');
      };

      video.addEventListener('ended', () => setPlaying(false));
      video.addEventListener('pause', () => setPlaying(false));
      video.addEventListener('play', () => setPlaying(true));

      slide.querySelectorAll('[data-cpg-video-seek]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const delta = parseInt(btn.dataset.cpgVideoSeek, 10) || 0;
          const apply = () => {
            const duration = Number.isFinite(video.duration) ? video.duration : video.currentTime + Math.abs(delta);
            video.currentTime = Math.min(Math.max(0, (video.currentTime || 0) + delta), duration);
          };
          if (video.readyState >= 1) {
            apply();
          } else {
            video.preload = 'metadata';
            video.addEventListener('loadedmetadata', apply, { once: true });
            video.load();
          }
        });
      });

      const soundToggle = slide.querySelector('[data-cpg-video-sound]');
      if (soundToggle) {
        const updateSoundToggle = () => {
          const soundOn = !video.muted;
          soundToggle.classList.toggle('is-sound-on', soundOn);
          soundToggle.setAttribute('aria-pressed', soundOn ? 'true' : 'false');
          soundToggle.setAttribute('aria-label', soundOn ? 'Mute sound' : 'Turn sound on');
        };

        soundToggle.addEventListener('pointerdown', (e) => {
          e.stopPropagation();
        });

        soundToggle.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          video.muted = !video.muted;
          if (!video.muted) video.volume = 1;
          updateSoundToggle();
        });

        video.addEventListener('volumechange', updateSoundToggle);
        updateSoundToggle();
      }
    });
  }

  bindExternalVideos() {
    this.slides.forEach((slide) => {
      if (slide.dataset.type !== 'external_video') return;
      const playBtn = slide.querySelector('[data-cpg-external-play]');
      const placeholder = slide.querySelector('[data-cpg-external-placeholder]');
      if (!playBtn || !placeholder) return;

      playBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const host = slide.dataset.host;
        const id = slide.dataset.externalId;
        let src = '';
        if (host === 'youtube') {
          src = 'https://www.youtube.com/embed/' + id + '?autoplay=1&rel=0';
        } else if (host === 'vimeo') {
          src = 'https://player.vimeo.com/video/' + id + '?autoplay=1';
        }
        if (!src) return;

        const iframe = document.createElement('iframe');
        iframe.src = src;
        iframe.allow = 'autoplay; encrypted-media; fullscreen';
        iframe.allowFullscreen = true;
        iframe.style.cssText = 'width:100%;height:100%;border:0;position:absolute;inset:0;';
        placeholder.innerHTML = '';
        placeholder.appendChild(iframe);
        this.stopAutoplay();
      });
    });
  }

  bindZoom() {
    if (!this.stage || !this.zoomEnabled) return;

    // 整屏看图里的图片列表（视频 slide 不进），记住每张对应的 slide index 好回来同步主图
    this.zoomItems = this.slides
      .filter((s) => s.dataset.type === 'image' && !s.hasAttribute('data-cpg-video-slot'))
      .map((s) => {
        const img = s.querySelector('.cpg-media--image');
        return {
          slideIndex: parseInt(s.dataset.index, 10),
          src: img ? img.dataset.cpgZoomSrc || img.currentSrc || img.src : '',
          alt: img ? img.alt || '' : '',
        };
      })
      .filter((it) => it.src);

    if (!this.zoomItems.length) return;

    this.stage.addEventListener('click', (e) => {
      if (this._suppressClick) return;
      const blockedTarget = e.target.closest(
        'button, a, iframe, video, .cpg-video-toggle, .cpg-video-controls, .cpg-video-sound, .cpg-media-dock, .cpg-nav, .cpg-click-zone, .cpg-thumb'
      );
      if (blockedTarget) return;

      const activeSlide = this.slides[this.currentIndex];
      if (!activeSlide || activeSlide.dataset.type !== 'image') return;

      this.openZoomAt(this.currentIndex);
    });
  }

  /** 打开整屏看图。参数是主图 slide 的 index；不是图片则从最近的一张开始。 */
  openZoomAt(slideIndex) {
    if (!this.zoomItems.length) return;
    let idx = this.zoomItems.findIndex((it) => it.slideIndex === slideIndex);
    if (idx < 0) idx = 0;

    this.ensureZoomModal();
    this.zoomModal.style.setProperty(
      '--cpg-modal-zoom-scale',
      this.root.style.getPropertyValue('--cpg-zoom-scale-desktop').trim() || '2'
    );
    this.zoomModal.style.setProperty(
      '--cpg-modal-zoom-scale-mobile',
      this.root.style.getPropertyValue('--cpg-zoom-scale-mobile').trim() || '2'
    );
    this.zoomModal.dataset.count = String(this.zoomItems.length);

    this._zoomShow(idx);
    this.zoomModal.classList.add('is-open');
    this.zoomModal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cpg-zoom-lock');
    this.stopAutoplay();

    const closeBtn = this.zoomModal.querySelector('[data-cpg-zoom-close]');
    if (closeBtn) closeBtn.focus({ preventScroll: true });
  }

  /** 兼容旧调用：传 img 元素 */
  openZoom(sourceImg) {
    const slide = sourceImg && sourceImg.closest ? sourceImg.closest('[data-cpg-slide]') : null;
    this.openZoomAt(slide ? parseInt(slide.dataset.index, 10) : this.currentIndex);
  }

  _zoomShow(idx) {
    const total = this.zoomItems.length;
    if (idx < 0) idx = total - 1;
    if (idx >= total) idx = 0;
    this.zoomIndex = idx;

    const item = this.zoomItems[idx];
    const modal = this.zoomModal;
    const img = modal.querySelector('[data-cpg-zoom-img]');
    const counter = modal.querySelector('[data-cpg-zoom-counter]');

    modal.classList.remove('is-zoomed', 'is-panning');
    img.style.setProperty('--cpg-tx', '0px');
    img.style.setProperty('--cpg-ty', '0px');
    if (img.src !== item.src) img.src = item.src;
    img.alt = item.alt;
    if (counter) counter.textContent = `${idx + 1} / ${total}`;
    const loupe = modal.querySelector('[data-cpg-zoom-toggle]');
    if (loupe) loupe.setAttribute('aria-label', 'Zoom in');

    // 预热左右两张
    [idx - 1, idx + 1].forEach((n) => {
      const it = this.zoomItems[(n + total) % total];
      if (it && it.src) {
        const pre = new Image();
        pre.decoding = 'async';
        pre.src = it.src;
      }
    });

    // 主图跟着走，关掉之后停在同一张
    if (item.slideIndex !== this.currentIndex) this.go(item.slideIndex);

    // 手机端把 × 贴到图片上边；图未加载完时等 load 再算
    const place = () => this._zoomPlaceControls();
    if (img.complete && img.naturalWidth) place();
    else img.addEventListener('load', place, { once: true });
  }

  /**
   * 按 object-fit:contain 的实际画面框，把手机端 × 放到图顶上方。
   * 桌面端不动（CSS 仍用固定 16px）；手机放大镜已隐藏。
   */
  _zoomPlaceControls() {
    const modal = this.zoomModal;
    if (!modal) return;
    const img = modal.querySelector('[data-cpg-zoom-img]');
    const viewport = modal.querySelector('[data-cpg-zoom-viewport]');
    if (!img || !viewport || !img.naturalWidth) return;

    const rect = viewport.getBoundingClientRect();
    const fit = Math.min(rect.width / img.naturalWidth, rect.height / img.naturalHeight);
    const contentH = img.naturalHeight * fit;
    const padY = Math.max(0, (rect.height - contentH) / 2);
    const btn = window.matchMedia('(max-width: 749px)').matches ? 34 : 90;
    const gap = 8;
    // 贴在灰边里、紧挨图片边缘；灰边不够高就退回最小边距
    const inset = Math.max(12, Math.round(padY - btn - gap));
    modal.style.setProperty('--cpg-close-top', `${inset}px`);
    modal.style.setProperty('--cpg-loupe-bottom', `${inset}px`);
  }

  _zoomStep(delta) {
    this._zoomShow(this.zoomIndex + delta);
  }

  ensureZoomModal() {
    let modal = document.querySelector('[data-cpg-zoom-modal]');

    if (!modal) {
      modal = document.createElement('div');
      modal.className = 'cpg-zoom-modal';
      modal.setAttribute('data-cpg-zoom-modal', '');
      modal.setAttribute('aria-hidden', 'true');
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.setAttribute('aria-label', 'Product image viewer');

      modal.innerHTML = `
        <button type="button" class="cpg-zoom-modal__close" data-cpg-zoom-close aria-label="Close image viewer">
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 3l10 10M13 3L3 13"/></svg>
        </button>
        <div class="cpg-zoom-modal__viewport" data-cpg-zoom-viewport>
          <img class="cpg-zoom-modal__img" data-cpg-zoom-img alt="" draggable="false">
        </div>
        <button type="button" class="cpg-zoom-modal__nav cpg-zoom-modal__nav--prev" data-cpg-zoom-prev aria-label="Previous image">
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M10 2L4 8l6 6"/></svg>
        </button>
        <button type="button" class="cpg-zoom-modal__nav cpg-zoom-modal__nav--next" data-cpg-zoom-next aria-label="Next image">
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M6 2l6 6-6 6"/></svg>
        </button>
        <button type="button" class="cpg-zoom-modal__loupe" data-cpg-zoom-toggle aria-label="Zoom in">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="10.5" cy="10.5" r="6.25"/>
            <path d="M10.5 7.75v5.5M7.75 10.5h5.5"/>
            <path d="M15.25 15.25L20 20"/>
          </svg>
        </button>
        <div class="cpg-zoom-modal__counter" data-cpg-zoom-counter aria-live="polite"></div>
      `;

      document.body.appendChild(modal);
    } else if (!modal.querySelector('[data-cpg-zoom-toggle]')) {
      const loupe = document.createElement('button');
      loupe.type = 'button';
      loupe.className = 'cpg-zoom-modal__loupe';
      loupe.setAttribute('data-cpg-zoom-toggle', '');
      loupe.setAttribute('aria-label', 'Zoom in');
      loupe.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.25"/><path d="M10.5 7.75v5.5M7.75 10.5h5.5"/><path d="M15.25 15.25L20 20"/></svg>';
      modal.appendChild(loupe);
    }

    // 每个画廊实例都要把控件绑到自己身上；modal 是全页共享的，所以先把旧的 owner 解绑
    if (modal._cpgOwner && modal._cpgOwner !== this && typeof modal._cpgUnbind === 'function') {
      modal._cpgUnbind();
    }
    if (modal._cpgOwner !== this) {
      this._bindZoomModal(modal);
    }

    this.zoomModal = modal;
  }

  _bindZoomModal(modal) {
    const viewport = modal.querySelector('[data-cpg-zoom-viewport]');
    const img = modal.querySelector('[data-cpg-zoom-img]');
    const closeBtn = modal.querySelector('[data-cpg-zoom-close]');
    const prevBtn = modal.querySelector('[data-cpg-zoom-prev]');
    const nextBtn = modal.querySelector('[data-cpg-zoom-next]');
    const loupeBtn = modal.querySelector('[data-cpg-zoom-toggle]');

    const isOpen = () => modal.classList.contains('is-open');
    const isZoomed = () => modal.classList.contains('is-zoomed');

    const close = () => {
      modal.classList.remove('is-open', 'is-zoomed', 'is-panning');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('cpg-zoom-lock');
      if (this.autoplay) this.startAutoplay();
      this.root.focus({ preventScroll: true });
    };

    // 平移状态：tx/ty 是图片中心相对视口中心的位移（px）。每帧只写一次 CSS 变量。
    let tx = 0, ty = 0, rafId = 0;

    const currentScale = () => {
      const s = parseFloat(getComputedStyle(img).getPropertyValue('--cpg-s'));
      return Number.isFinite(s) && s > 0 ? s : 1;
    };

    // 能拖的最远距离 = 放大后整个画面框（<img> 元素是 100vw×100vh）溢出视口的一半。
    // 故意不按 object-fit 内容框算：方图在宽屏里内容框横向只多出 120px，几乎不能左右拖；
    // 按画面框算横竖都能拖满，露出来的是灰渐变底，本来就是要的效果。
    const clamp = () => {
      const rect = viewport.getBoundingClientRect();
      const s = currentScale();
      const maxX = Math.max(0, (rect.width * s - rect.width) / 2);
      const maxY = Math.max(0, (rect.height * s - rect.height) / 2);
      tx = Math.min(Math.max(tx, -maxX), maxX);
      ty = Math.min(Math.max(ty, -maxY), maxY);
    };

    const paint = () => {
      rafId = 0;
      img.style.setProperty('--cpg-tx', `${tx}px`);
      img.style.setProperty('--cpg-ty', `${ty}px`);
    };
    const schedule = () => {
      if (!rafId) rafId = requestAnimationFrame(paint);
    };

    const resetPan = () => {
      tx = 0; ty = 0;
      if (rafId) { cancelAnimationFrame(rafId); rafId = 0; }
      paint();
    };

    const toggleZoom = (clientX, clientY) => {
      if (isZoomed()) {
        modal.classList.remove('is-zoomed', 'is-panning');
        resetPan();
        if (loupeBtn) loupeBtn.setAttribute('aria-label', 'Zoom in');
        return;
      }
      // 以点击点为中心放大：让点击处在放大后仍停在指针下
      modal.classList.add('is-zoomed');
      const rect = viewport.getBoundingClientRect();
      const s = currentScale();
      const px = clientX - rect.left - rect.width / 2;
      const py = clientY - rect.top - rect.height / 2;
      tx = (1 - s) * px;
      ty = (1 - s) * py;
      clamp();
      paint();
      if (loupeBtn) loupeBtn.setAttribute('aria-label', 'Zoom out');
    };

    // 点：切放大/还原。滑：换图（未放大时）。放大后按住拖动 = 图片跟着手指/鼠标走。
    let downX = 0, downY = 0, downT = 0, startTx = 0, startTy = 0, moved = false, pointerId = null;

    const onDown = (e) => {
      if (e.target.closest('button')) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      downX = e.clientX; downY = e.clientY; downT = Date.now(); moved = false; pointerId = e.pointerId;
      startTx = tx; startTy = ty;
      if (isZoomed()) {
        modal.classList.add('is-panning');
        try { viewport.setPointerCapture(e.pointerId); } catch (err) {}
      }
      e.preventDefault();
    };
    const onMove = (e) => {
      if (pointerId === null || e.pointerId !== pointerId) return;
      const dx = e.clientX - downX;
      const dy = e.clientY - downY;
      if (Math.abs(dx) > 6 || Math.abs(dy) > 6) moved = true;
      if (!isZoomed()) return;
      tx = startTx + dx;
      ty = startTy + dy;
      clamp();
      schedule();
    };
    const endPointer = (e) => {
      pointerId = null;
      modal.classList.remove('is-panning');
      try { viewport.releasePointerCapture(e.pointerId); } catch (err) {}
    };
    const onUp = (e) => {
      if (pointerId === null || e.pointerId !== pointerId) return;
      endPointer(e);
      if (e.target.closest('button')) return;

      const dx = e.clientX - downX;
      const dy = e.clientY - downY;
      const quick = Date.now() - downT < 600;

      if (!isZoomed() && Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) && quick) {
        this._zoomStep(dx < 0 ? 1 : -1);
        return;
      }
      if (!moved) toggleZoom(e.clientX, e.clientY);
    };
    const onCancel = (e) => {
      if (pointerId === null || e.pointerId !== pointerId) return;
      endPointer(e);
    };

    const onKey = (e) => {
      if (!isOpen()) return;
      if (e.key === 'Escape') { e.preventDefault(); close(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); this._zoomStep(-1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); this._zoomStep(1); }
    };

    const onClose = () => close();
    const onPrev = (e) => { e.stopPropagation(); this._zoomStep(-1); };
    const onNext = (e) => { e.stopPropagation(); this._zoomStep(1); };
    const onLoupe = (e) => {
      e.stopPropagation();
      const rect = viewport.getBoundingClientRect();
      toggleZoom(rect.left + rect.width / 2, rect.top + rect.height / 2);
    };
    const onResize = () => {
      if (isOpen()) this._zoomPlaceControls();
    };

    viewport.addEventListener('pointerdown', onDown);
    viewport.addEventListener('pointermove', onMove);
    viewport.addEventListener('pointerup', onUp);
    viewport.addEventListener('pointercancel', onCancel);
    closeBtn.addEventListener('click', onClose);
    prevBtn.addEventListener('click', onPrev);
    nextBtn.addEventListener('click', onNext);
    if (loupeBtn) loupeBtn.addEventListener('click', onLoupe);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);

    modal._cpgOwner = this;
    modal._cpgUnbind = () => {
      viewport.removeEventListener('pointerdown', onDown);
      viewport.removeEventListener('pointermove', onMove);
      viewport.removeEventListener('pointerup', onUp);
      viewport.removeEventListener('pointercancel', onCancel);
      closeBtn.removeEventListener('click', onClose);
      prevBtn.removeEventListener('click', onPrev);
      nextBtn.removeEventListener('click', onNext);
      if (loupeBtn) loupeBtn.removeEventListener('click', onLoupe);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
      modal._cpgOwner = null;
      modal._cpgUnbind = null;
    };
  }

  bindKeyboard() {
    this.root.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.currentIndex - 1); this.resetAutoplay(); }
      if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.currentIndex + 1); this.resetAutoplay(); }
    });
    if (!this.root.hasAttribute('tabindex')) this.root.setAttribute('tabindex', '0');
  }

  bindTouch() {
    let startX = 0;
    let startY = 0;
    let lastX = 0;
    let lastT = 0;
    let velocity = 0;
    let tracking = false;
    let axis = '';

    const reset = () => {
      tracking = false;
      axis = '';
      velocity = 0;
    };

    this.stage.addEventListener('touchstart', (e) => {
      if (e.touches.length !== 1) return;
      if (e.target.closest('button, a, input, textarea, select, .cpg-media-dock')) return;
      const point = e.touches[0];
      startX = lastX = point.clientX;
      startY = point.clientY;
      lastT = performance.now();
      velocity = 0;
      tracking = true;
      axis = '';
      this.stopAutoplay();
      if (this._isMobileStrip()) this._placeTrack(this.currentIndex, { animate: false });
    }, { passive: true });

    this.stage.addEventListener('touchmove', (e) => {
      if (!tracking || e.touches.length !== 1 || !this._isMobileStrip()) return;
      const point = e.touches[0];
      const dx = point.clientX - startX;
      const dy = point.clientY - startY;
      const now = performance.now();
      velocity = (point.clientX - lastX) / Math.max(1, now - lastT);
      lastX = point.clientX;
      lastT = now;

      if (!axis) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (axis === 'x') this._preloadAdjacent(this.currentIndex);
      }
      if (axis !== 'x') return;
      if (e.cancelable) e.preventDefault();

      let drag = dx;
      const atStart = this.currentIndex <= 0 && drag > 0;
      const atEnd = this.currentIndex >= this.total - 1 && drag < 0;
      if (atStart || atEnd) drag *= 0.32;
      this._placeTrack(this.currentIndex, { animate: false, drag });
    }, { passive: false });

    const finish = (e) => {
      if (!tracking) return;
      const point = e.changedTouches ? e.changedTouches[0] : null;
      const dx = point ? point.clientX - startX : 0;
      const dy = point ? point.clientY - startY : 0;
      const mobile = this._isMobileStrip();
      const horizontal = axis === 'x';
      reset();

      if (mobile && horizontal) {
        const width = this.stage.clientWidth || 1;
        const threshold = Math.min(72, Math.max(36, width * 0.16));
        const goNext = (dx <= -threshold || (velocity < -0.55 && dx < -12)) && this.currentIndex < this.total - 1;
        const goPrev = (dx >= threshold || (velocity > 0.55 && dx > 12)) && this.currentIndex > 0;
        if (Math.abs(dx) > 8) this._armClickSuppress();
        if (goNext) this.go(this.currentIndex + 1);
        else if (goPrev) this.go(this.currentIndex - 1);
        else this._placeTrack(this.currentIndex, { animate: true });
      } else if (!mobile && Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 50) {
        this.go(dx > 0 ? this.currentIndex - 1 : this.currentIndex + 1);
      }

      if (this.autoplay) this.startAutoplay();
    };

    this.stage.addEventListener('touchend', finish, { passive: true });
    this.stage.addEventListener('touchcancel', finish, { passive: true });
  }

  startAutoplay() {
    if (!this.autoplay || this.total <= 1) return;
    this.stopAutoplay();
    this.autoplayTimer = setInterval(() => {
      this.go(this.currentIndex + 1);
    }, this.autoplayInterval);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  resetAutoplay() {
    if (this.autoplay) {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }

  go(target) {
    let next = target;
    if (next < 0) next = this.total - 1;
    if (next >= this.total) next = 0;
    if (next === this.currentIndex) return;

    // 【关键修复位置】：缓存原先的索引，避免提前覆盖
    const prevIndex = this.currentIndex;

    // 暂停当前视频,并复位回封面图:再切回来时显示的是封面,不是暂停帧
    const currentSlide = this.slides[prevIndex];
    if (currentSlide && currentSlide.dataset.type === 'video') {
      const v = currentSlide.querySelector('[data-cpg-video]');
      if (v) {
        if (!v.paused) v.pause();
        try { v.currentTime = 0; } catch (err) {}
      }
      currentSlide.classList.remove('is-video-playing');
    }

    // 手机：相邻两张平移；跨多张（含首尾回绕）直接跳，避免整条带子扫过去
    if (this._isMobileStrip()) {
      this._placeTrack(next, { animate: Math.abs(next - prevIndex) === 1 });
    }

    // 使用 requestAnimationFrame 批量更新 DOM
    requestAnimationFrame(() => {
      this.slides[prevIndex].classList.remove('is-active');
      this.slides[prevIndex].setAttribute('aria-hidden', 'true');
      this.slides[next].classList.add('is-active');
      this.slides[next].setAttribute('aria-hidden', 'false');

      if (this.thumbsByIndex[prevIndex]) {
        this.thumbsByIndex[prevIndex].forEach((t) => t.classList.remove('is-active'));
      }
      if (this.thumbsByIndex[next]) {
        this.thumbsByIndex[next].forEach((t) => {
          t.classList.add('is-active');
          // 如果是外部缩略图且不可见，自动平滑滚动将其带入视野
          if (t.classList.contains('cpg-thumb--external')) {
            t.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
          }
        });
      }

      if (this.ticks.length) {
        this.ticks[prevIndex].classList.remove('is-active');
        this.ticks[next].classList.add('is-active');
      }

      if (this.numberCurrent) {
        this.numberCurrent.classList.add('is-switching');
        setTimeout(() => {
          const num = next + 1;
          this.numberCurrent.textContent = (num < 10 ? '0' : '') + num;
          this.numberCurrent.classList.remove('is-switching');
        }, 150);
      }
    });

    this.currentIndex = next;
    this._preloadAdjacent(next);
  }

  goToMedia(media) {
    if (!media) return false;

    var id = media.id;
    if (id != null) {
      var idText = String(id);
      var idTail = idText.split('/').pop();
      var byId = this.slides.findIndex(function (slide) {
        var slideId = String(slide.dataset.mediaId || '');
        return slideId === idText || slideId === idTail;
      });
      if (byId >= 0) {
        this.go(byId);
        return true;
      }
    }

    var src = media.preview_image && media.preview_image.src;
    if (src) {
      var file = src.split('/').pop().split('?')[0];
      var bySrc = this.slides.findIndex(function (slide) {
        var img = slide.querySelector('img');
        return img && img.src.indexOf(file) !== -1;
      });
      if (bySrc >= 0) {
        this.go(bySrc);
        return true;
      }
    }

    return false;
  }
}

if (!window.__cpgVariantBound) {
  window.__cpgVariantBound = true;
  document.addEventListener('variant:update', function (event) {
    var media = event.detail && event.detail.resource && event.detail.resource.featured_media;
    if (!media) return;
    document.querySelectorAll('[data-cpg-root]').forEach(function (root) {
      if (root._cpg && typeof root._cpg.goToMedia === 'function') {
        root._cpg.goToMedia(media);
      }
    });
  });
}

/** @param {ParentNode} [scope] */
export function initGalleries(scope = document) {
  scope.querySelectorAll('[data-cpg-root]').forEach((root) => {
    if (root.dataset.cpgInitialized === '1') return;
    root.dataset.cpgInitialized = '1';
    new CustomProductGallery(root);
  });
}

function initAll() {
  initGalleries(document);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll);
} else {
  initAll();
}

document.addEventListener('shopify:section:load', initAll);
document.addEventListener('shopify:block:select', initAll);
